import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Loader2, ArrowRight, MapPin, Package, Calendar, MessageCircle, Mail, AlertCircle, Check, Clock } from 'lucide-react';
import SEO from '../components/SEO';
import QuotesGrid from '../components/QuotesGrid';
import { rfqTracking, MODE } from '../lib/dataClient';
import { RFQ_STATUS_ORDER, RFQ_STATUS_META, indexOfStatus, isTerminal } from '../lib/rfqStatus';
import { track, EVENTS } from '../lib/analytics';
import { authClient } from '../lib/authClient';

/**
 * /rfq/:reference_code
 *
 * Buyer-facing operational tracking dashboard for an RFQ. Reads the RFQ
 * record + status_events from the dataClient. Holders of the reference
 * code can view the page (no auth required — same model as parcel
 * tracking) so RFQs filed before signup remain accessible.
 *
 * The `?dev=1` query toggle exposes mock-only admin helpers (advance
 * status, seed full timeline) so the design + testing team can walk
 * the full lifecycle without a real backend.
 *
 * SEO: noindex (a unique tracking URL is per-buyer).
 */

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });
};

const StatusBadge = ({ status }) => {
  const meta = RFQ_STATUS_META[status] || {};
  const terminal = isTerminal(status);
  return (
    <span
      data-testid="rfq-tracking-current-status"
      className={`inline-flex items-center gap-2 border px-3 py-1 font-mono-data text-[10px] uppercase tracking-wider ${
        terminal ? 'border-neutral-400 bg-neutral-100 text-neutral-700' : 'border-black bg-black text-white'
      }`}
    >
      <span className={`block h-1.5 w-1.5 rounded-full ${terminal ? 'bg-neutral-500' : 'bg-emerald-400'}`} />
      {meta.short || status}
    </span>
  );
};

const RfqTracking = () => {
  const { reference_code } = useParams();
  const [search] = useSearchParams();
  const devMode = search.get('dev') === '1' && MODE === 'mock';

  const [loading, setLoading] = useState(true);
  const [rfq, setRfq]         = useState(null);
  const [events, setEvents]   = useState([]);
  const [err, setErr]         = useState('');
  const [advancing, setAdvancing] = useState(false);
  const [viewerEmail, setViewerEmail] = useState('');

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const session = await authClient.getSession();
        const email = session?.user?.email || '';
        if (alive) setViewerEmail(email);
      } catch {
        if (alive) setViewerEmail('');
      }
    })();
    return () => { alive = false; };
  }, []);

  const refresh = useCallback(async () => {
    try {
      const [r, ev] = await Promise.all([
        rfqTracking.getByReference(reference_code),
        rfqTracking.listStatusEvents(reference_code),
      ]);
      setRfq(r);
      setEvents(ev);
      if (r) {
        track(EVENTS.RFQ_TRACKING_VIEWED, { reference_code, status: r.status });
        const qcount = Array.isArray(r?.quotes_json) ? r.quotes_json.length : 0;
        if (qcount > 0) track(EVENTS.BUYER_QUOTE_VIEWED, { reference_code, count: qcount, status: r.status });
      } else { track(EVENTS.RFQ_TRACKING_NOT_FOUND, { reference_code }); }
    } catch (e) {
      console.error('[rfq-tracking]', e);
      setErr(e?.message || 'Could not load this RFQ.');
    } finally {
      setLoading(false);
    }
  }, [reference_code]);

  useEffect(() => { refresh(); }, [refresh]);

  const handleAdvance = async () => {
    if (advancing) return;
    setAdvancing(true);
    try {
      const r = await rfqTracking.advanceMockStatus(reference_code);
      if (r.ok) await refresh();
    } finally { setAdvancing(false); }
  };
  const handleSeedFull = async () => {
    if (advancing) return;
    setAdvancing(true);
    try { await rfqTracking.seedMockFullTimeline(reference_code); await refresh(); }
    finally { setAdvancing(false); }
  };

  const currentIdx = useMemo(() => rfq ? indexOfStatus(rfq.status) : -1, [rfq]);
  const currentMeta = rfq ? (RFQ_STATUS_META[rfq.status] || {}) : null;

  // ─── Loading state ─────────────────────────────────────────────────
  if (loading) {
    return (
      <div data-testid="rfq-tracking-loading" className="flex min-h-[60vh] items-center justify-center bg-white">
        <div className="text-center">
          <Loader2 size={20} strokeWidth={1.5} className="mx-auto animate-spin text-neutral-500" />
          <div className="mt-3 label-overline text-neutral-500">Loading RFQ {reference_code}</div>
        </div>
      </div>
    );
  }

  // ─── Not-found / error state ───────────────────────────────────────
  if (!rfq) {
    return (
      <div data-testid="rfq-tracking-not-found" className="bg-white">
        <SEO title={`RFQ ${reference_code} not found`} description="No RFQ matches this reference code." noindex />
        <section className="border-b border-black">
          <div className="mx-auto max-w-2xl px-5 py-24 text-center md:py-32">
            <AlertCircle size={20} strokeWidth={1.5} className="mx-auto text-neutral-500" />
            <span className="mt-6 inline-block label-overline">Tracking · not found</span>
            <h1 className="mt-4 font-display tracking-tighter" style={{ fontWeight: 700, fontSize: 'clamp(2rem, 5vw, 4rem)', lineHeight: 0.95 }}>
              No RFQ matches this reference.
            </h1>
            <p className="mt-5 text-neutral-700">We couldn't find an RFQ for <span className="font-mono-data text-black">{reference_code}</span>. The link may be mistyped, the request may have been removed, or you may be looking at an older browser session in mock mode.</p>
            {err && <div className="mt-4 border border-black bg-neutral-50 px-3 py-2 text-xs">{err}</div>}
            <div className="mt-10 flex flex-col justify-center gap-3 sm:flex-row">
              <Link to="/rfq" className="btn-giwahs btn-giwahs-secondary">Submit a new RFQ</Link>
              <Link to="/contact" className="btn-giwahs btn-giwahs-primary">Contact support</Link>
            </div>
          </div>
        </section>
      </div>
    );
  }

  // ─── Loaded state ──────────────────────────────────────────────────
  const moqLine = rfq.target_quantity ? `${(rfq.target_quantity).toLocaleString?.() || rfq.target_quantity} units`
                : rfq.quantity ? `${rfq.quantity.toLocaleString?.() || rfq.quantity} units` : '—';
  const category = rfq.product_type || rfq.category || rfq.product_name || '—';
  const sourcingRegion = rfq.preferred_countries?.length ? rfq.preferred_countries.join(', ')
                       : rfq.shipping_destination || rfq.sourcing_region || rfq.destination_country || rfq.region || '—';
  const timelineLine = rfq.timeline || rfq.needed_by_date || '';

  return (
    <div data-testid="rfq-tracking-page" className="bg-white">
      <SEO title={`RFQ ${rfq.reference_code} — Tracking`} description="GIWAHS RFQ tracking dashboard." noindex />

      {/* HEADER */}
      <section className="border-b border-black bg-white">
        <div className="mx-auto max-w-[1440px] px-5 py-10 md:px-8 md:py-14">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <span className="label-overline text-neutral-500">/ RFQ · Tracking</span>
              <div className="mt-3 flex flex-wrap items-baseline gap-4">
                <h1 className="font-display tracking-tighter" style={{ fontWeight: 700, fontSize: 'clamp(2rem, 5vw, 4rem)', lineHeight: 0.95 }}>
                  {rfq.reference_code}
                </h1>
                <StatusBadge status={rfq.status} />
              </div>
              <p className="mt-4 max-w-xl text-sm text-neutral-600">{currentMeta?.description}</p>
              <div className="mt-5 inline-flex items-center gap-2 border border-neutral-300 bg-[#F5F5F0] px-3 py-2">
                <Clock size={12} strokeWidth={1.5} className="text-neutral-500" />
                <span className="label-overline text-neutral-500">Estimated next update</span>
                <span className="text-xs text-neutral-700">{currentMeta?.estimate}</span>
              </div>
            </div>

            {devMode && (
              <div data-testid="rfq-tracking-dev-tools" className="border border-dashed border-neutral-400 bg-neutral-50 p-3">
                <span className="label-overline text-neutral-500">Dev tools (mock)</span>
                <div className="mt-2 flex flex-wrap gap-2">
                  <button data-testid="rfq-tracking-advance-btn" onClick={handleAdvance} disabled={advancing || isTerminal(rfq.status)} className="border border-black bg-white px-3 py-1.5 text-xs hover:bg-black hover:text-white disabled:cursor-not-allowed disabled:opacity-50">
                    {advancing ? <Loader2 size={12} className="inline animate-spin" /> : 'Advance status'} <ArrowRight size={10} className="ml-1 inline" strokeWidth={1.5} />
                  </button>
                  <button data-testid="rfq-tracking-seed-btn" onClick={handleSeedFull} disabled={advancing} className="border border-black bg-white px-3 py-1.5 text-xs hover:bg-black hover:text-white disabled:opacity-50">
                    Seed full timeline
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* MAIN GRID */}
      <section className="bg-white">
        <div className="mx-auto grid max-w-[1440px] gap-10 px-5 py-10 md:grid-cols-12 md:px-8 md:py-14">

          {/* TIMELINE — left/main */}
          <div className="md:col-span-7" data-testid="rfq-tracking-timeline">
            <span className="label-overline">Lifecycle</span>
            <h2 className="mt-2 font-display text-2xl tracking-tight">Sourcing timeline.</h2>

            <ol className="mt-8 border-l border-black">
              {RFQ_STATUS_ORDER.map((status, i) => {
                const meta = RFQ_STATUS_META[status];
                const event = events.find(e => e.status === status);
                const reached = i <= currentIdx;
                const isCurrent = i === currentIdx && !isTerminal(rfq.status);
                return (
                  <li key={status} data-testid={`rfq-tracking-step-${status}`} className={`relative -ml-px border-l-[3px] pl-6 pr-2 pb-8 ${reached ? 'border-black' : 'border-neutral-300'}`}>
                    <span className={`absolute -left-[7px] top-0 grid h-3 w-3 place-items-center border ${reached ? 'border-black bg-black' : 'border-neutral-300 bg-white'}`}>
                      {reached && <Check size={8} strokeWidth={3} className="text-white" />}
                    </span>
                    <div className="flex flex-wrap items-baseline gap-3">
                      <span className={`label-overline ${reached ? 'text-black' : 'text-neutral-400'}`}>Step {i + 1}</span>
                      <span className={`font-display text-base ${reached ? '' : 'text-neutral-400'}`}>{meta.label}</span>
                      {isCurrent && <span className="border border-black bg-emerald-400 px-2 py-0.5 font-mono-data text-[10px] uppercase tracking-wider text-black">Now</span>}
                    </div>
                    <p className={`mt-2 text-sm ${reached ? 'text-neutral-700' : 'text-neutral-400'}`}>{event?.note || meta.description}</p>
                    {event?.created_at && (
                      <div className="mt-2 font-mono-data text-[10px] uppercase tracking-wider text-neutral-500">{formatDate(event.created_at)}{event.actor === 'system' ? ' · automated' : event.actor === 'admin' ? ' · sourcing desk' : ''}</div>
                    )}
                  </li>
                );
              })}
            </ol>
          </div>

          {/* SIDEBAR — right */}
          <aside className="md:col-span-5 space-y-6">
            {/* RFQ summary panel */}
            <div data-testid="rfq-tracking-summary" className="border border-black p-6">
              <span className="label-overline">RFQ summary</span>
              <h3 className="mt-2 font-display text-xl">Your sourcing brief.</h3>
              <dl className="mt-5 space-y-3 text-sm">
                <div className="flex items-start gap-3 border-b border-neutral-200 pb-3">
                  <Package size={14} strokeWidth={1.5} className="mt-0.5 text-neutral-500" />
                  <div className="flex-1">
                    <dt className="label-overline text-neutral-500">Category</dt>
                    <dd>{category}</dd>
                  </div>
                </div>
                <div className="flex items-start gap-3 border-b border-neutral-200 pb-3">
                  <span className="mt-0.5 inline-block font-mono-data text-[10px] uppercase tracking-wider text-neutral-500">MOQ</span>
                  <div className="flex-1">
                    <dt className="label-overline text-neutral-500">Quantity</dt>
                    <dd data-testid="rfq-tracking-moq">{moqLine}</dd>
                  </div>
                </div>
                <div className="flex items-start gap-3 border-b border-neutral-200 pb-3">
                  <MapPin size={14} strokeWidth={1.5} className="mt-0.5 text-neutral-500" />
                  <div className="flex-1">
                    <dt className="label-overline text-neutral-500">Sourcing region preference</dt>
                    <dd>{sourcingRegion}</dd>
                  </div>
                </div>
                {timelineLine && (
                  <div className="flex items-start gap-3 border-b border-neutral-200 pb-3">
                    <Calendar size={14} strokeWidth={1.5} className="mt-0.5 text-neutral-500" />
                    <div className="flex-1">
                      <dt className="label-overline text-neutral-500">Timeline</dt>
                      <dd>{timelineLine}</dd>
                    </div>
                  </div>
                )}
                <div className="flex items-start gap-3">
                  <span className="mt-0.5 inline-block font-mono-data text-[10px] uppercase tracking-wider text-neutral-500">Filed</span>
                  <div className="flex-1">
                    <dt className="label-overline text-neutral-500">Submitted</dt>
                    <dd>{formatDate(rfq.created_at)}</dd>
                  </div>
                </div>
              </dl>
            </div>

            {/* Supplier shortlist + real quotes */}
            <div data-testid="rfq-tracking-shortlist" className="border border-black p-6">
              <div className="flex items-end justify-between">
                <div>
                  <span className="label-overline">Supplier quotes</span>
                  <h3 className="mt-2 font-display text-xl">Quotes received.</h3>
                </div>
                {(rfq.quotes_json || []).length > 0 && (
                  <span data-testid="rfq-quotes-count" className="font-mono-data text-[10px] uppercase tracking-wider text-neutral-500">
                    {(rfq.quotes_json || []).length} quote{(rfq.quotes_json || []).length === 1 ? '' : 's'}
                  </span>
                )}
              </div>
              {/* Always render QuotesGrid — it carries its own empty-state (rfq-quotes-empty) */}
              <QuotesGrid
                quotes={rfq.quotes_json || []}
                reference={rfq.reference_code}
                canDecide={
                  MODE === 'supabase' &&
                  !!viewerEmail &&
                  !!rfq.buyer_email &&
                  viewerEmail.toLowerCase() === String(rfq.buyer_email).toLowerCase()
                }
                rfq={rfq}
                onDecided={refresh}
              />
              {currentIdx < indexOfStatus('supplier_shortlisting') && (rfq.quotes_json || []).length === 0 && (
                <p className="mt-3 text-xs text-neutral-500">A curated shortlist will appear here once the sourcing desk completes the matching pass.</p>
              )}
            </div>

            {/* Need help CTA */}
            <div data-testid="rfq-tracking-support" className="border border-black bg-black p-6 text-white">
              <span className="label-overline text-white/70">Need help?</span>
              <h3 className="mt-2 font-display text-xl">Talk to the sourcing desk.</h3>
              <p className="mt-3 text-sm text-white/80">Questions about your RFQ, supplier preferences, or timeline shifts — the GIWAHS team is one message away.</p>
              <div className="mt-5 flex flex-wrap gap-3">
                <Link
                  to={`/contact?topic=rfq&ref=${encodeURIComponent(rfq.reference_code)}`}
                  data-testid="rfq-tracking-contact-link"
                  onClick={() => track(EVENTS.RFQ_SUPPORT_CTA_CLICKED, { reference_code: rfq.reference_code, status: rfq.status, action: 'contact' })}
                  className="inline-flex items-center gap-2 border border-white bg-white px-3 py-2 text-xs font-medium uppercase tracking-wider text-black hover:bg-transparent hover:text-white"
                >
                  <MessageCircle size={12} strokeWidth={1.5} /> Open conversation
                </Link>
                <a
                  href={`mailto:hello@giwahs.com?subject=RFQ%20${encodeURIComponent(rfq.reference_code)}`}
                  data-testid="rfq-tracking-email-link"
                  onClick={() => track(EVENTS.RFQ_SUPPORT_CTA_CLICKED, { reference_code: rfq.reference_code, status: rfq.status, action: 'email' })}
                  className="inline-flex items-center gap-2 border border-white px-3 py-2 text-xs font-medium uppercase tracking-wider hover:bg-white hover:text-black"
                >
                  <Mail size={12} strokeWidth={1.5} /> Email desk
                </a>
              </div>
            </div>

            <div className="text-xs text-neutral-500">
              This is a private tracking link — anyone with the URL can view this page. Treat it like an order receipt: avoid sharing it on public channels.
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
};

export default RfqTracking;
